/**
 * Broker Accounts Page
 * Main component for displaying and managing broker accounts
 * Consolidated from previous ViewTradeAccounts
 *
 * LIVE, NOT FETCHED-AND-REFRESHED.
 * This screen reads `brokerSessions` from AuthContext, which seeds itself from
 * /broker-accounts and is then kept current by the `broker-session.{userId}` WS
 * topic (backend publishes session_created/updated/deleted -> Redis
 * broker:session:events:{userId} -> redis_bridge -> WS). That whole pipeline
 * already existed and nothing consumed it: this page used to run its OWN
 * duplicate fetch of the SAME endpoint and offer a manual Refresh button to
 * compensate. Session status now updates itself, so the button is gone.
 * Pull-to-refresh stays as a zero-chrome escape hatch (an account added on
 * another device emits no session event until it is logged in).
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Flag from "react-world-flags";
import AccountDetails from "./ViewTradeAccounts/AccountDetails";
import { PageHeader, Icon, PullToRefresh, Loader } from "../../components/ds";
import { useAuth } from "../../contexts/AuthContext";
import { accountStatusOf, sessionStatusOf, isAccountVerified } from "./accountStatus";
import AlertMessage from "./Components/AlertMessage";
import SessionStatusProgress from "./Components/SessionStatusProgress";
import LoginModal from "./Components/LoginModal";

const TradeAccounts = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();

  // The live broker-account state (see the note above) + its refetch, for PTR.
  const {
    brokerSessions: accounts,
    brokerSessionsLoading: loading,
    brokerSessionsRefreshing: refreshing,
    brokerSessionsError,
    fetchBrokerSessions,
  } = useAuth();

  // The error lives in the context now, so "dismiss" is local. Keyed on the message
  // so a NEW failure re-surfaces instead of being permanently silenced by one dismiss.
  const [dismissedError, setDismissedError] = useState("");
  const error =
    brokerSessionsError && brokerSessionsError !== dismissedError
      ? `Failed to load accounts: ${brokerSessionsError}`
      : "";

  // Login modal state
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [selectedAccountForLogin, setSelectedAccountForLogin] = useState(null);

  // Fetch when the page opens. This is the ORIGINAL behaviour, restored:
  //     useEffect(() => { if (!accountId) loadAccounts(); }, [accountId]);
  // I removed it when this screen moved onto AuthContext's brokerSessions, on the
  // reasoning that the WS keeps them live. That was wrong twice over:
  //   · AuthContext fetches ONCE, when auth resolves at app boot. Navigating here
  //     from the More menu mounts this component but re-fetches nothing, so the page
  //     showed whatever was true when the app first loaded — an account connected
  //     since then still read "Disconnected".
  //   · The WS only updates a tab that is subscribed AT THE MOMENT the event fires.
  //     A broker login redirects to the callback and lands in a different tab, so the
  //     tab you come back to can easily never have seen the event at all.
  // Opening a screen should load that screen's current state. That is not a Refresh
  // button (still gone) — the WS keeps it live while you are here, PullToRefresh is
  // the manual escape hatch, and this is just the page doing its job on arrival.
  useEffect(() => {
    if (!accountId) fetchBrokerSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // If accountId is provided, render AccountDetails component
  if (accountId) {
    return <AccountDetails />;
  }

  // Handle login modal
  const handleOpenLoginModal = (account) => {
    // Clear any existing broker login status data to prevent auto-processing
    // This ensures a fresh login flow when refreshing or re-authenticating
    localStorage.removeItem('brokerLoginStatus');
    localStorage.removeItem('brokerSessionStatus');
    sessionStorage.removeItem('brokerLoginStatus');
    
    setSelectedAccountForLogin(account);
    setLoginModalOpen(true);
  };

  const handleCloseLoginModal = () => {
    setLoginModalOpen(false);
    setSelectedAccountForLogin(null);
  };

  const handleLoginSuccess = (loginDetails) => {
    console.log(
      "Login successful for account:",
      selectedAccountForLogin?.account_id,
      loginDetails
    );
    // The broker publishes session_created, so the WS would land this anyway —
    // but this is the one moment the user is watching for it, so don't gamble on
    // a single event: refetch explicitly. (An explicit action refetching is not
    // the same thing as a standing Refresh button.)
    fetchBrokerSessions();
  };

  // Render account card
  const renderAccountCard = (account) => {
    // Read status through the ONE shared mapping (./accountStatus). This card used
    // to test `status === "verified"` and fall through to a hard-coded "Inactive"
    // for every other value — so an account whose status is "active" rendered
    // "Inactive" HERE and "ACTIVE" on its own detail page. Same field, same account,
    // opposite words.
    const accountStatus = accountStatusOf(account);
    const session = sessionStatusOf(account);
    const isConnected = session.isActive;

    return (
        <div
          key={account.account_id}
          className="
            h-auto
            bg-bg-2
            text-fg-1
            w-full
            p-4
            rounded-lg
            cursor-pointer
            card-hover
            shadow-sm
            border-l-4 border border-[var(--divider)]"
          style={{
            // borderLeftColor: isActive ? "#10b981" : "#ef4444",
            borderLeftColor: isConnected ? "var(--success)" : "var(--danger)",
          }}
          onClick={() => navigate(`/TradeAccounts/${account.account_id}`)}
          title="View account details"
        >
          {/* Account Holder Section */}
          <div className="mb-3 pb-3 border-b border-[var(--divider)]">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="bg-bg-3 p-1.5 rounded text-fg-2 shrink-0">
                  <Icon name="user" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-xs text-fg-2">
                    Account Holder
                  </span>
                  {/* truncate to the space actually available, not to an arbitrary
                      180px — the card is wider than that on a phone and narrower on
                      a dense 2xl grid. */}
                  <span className="text-sm font-semibold text-fg-1 truncate">
                    {account.account_holder_name || "Unknown Account"}
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0">
                {/* Bundled flag, not a flagcdn.com hotlink: the CDN version leaked the
                    user's IP + broker country to a third party on every render and
                    vanished silently offline (its onError just hid the img).
                    react-world-flags is already a dependency and is what the sibling
                    BrokerSelectionGrid in this same folder uses. */}
                {account.broker_country_code && (
                  <Flag
                    code={account.broker_country_code}
                    className="w-5 h-[15px] rounded-sm object-cover shadow-sm"
                    title={account.broker_country_code}
                    fallback={null}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Broker and Status Section */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="bg-bg-3 p-1.5 rounded text-fg-2">
                <Icon name="bank" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-fg-2">
                  Broker
                </span>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-fg-1">
                    {account.broker_display_name || "Unknown Broker"}
                  </span>
                  {account.broker_currency && (
                    <span className="text-xs text-fg-2 font-medium">
                      {account.broker_currency}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className={`ds-badge sm ${accountStatus.variant}`}>
              <Icon
                name={
                  accountStatus.variant === "profit"
                    ? "success"
                    : accountStatus.variant === "warn"
                    ? "pending"
                    : "error"
                }
                size={18}
              />
              <span>{accountStatus.label}</span>
            </div>
          </div>

          {/* Session Status Progress */}
          <div className="mb-3">
            {/* Takes the account and derives the session itself — it used to be handed
                the raw session_status, which meant this card leaked the one field the
                feature agreed to distrust straight past the shared module. */}
            <SessionStatusProgress
              account={account}
              size="small"
              className="w-full"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-3 pt-3 border-t border-[var(--divider)]">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/TradeAccounts/${account.account_id}`);
              }}
              className="btn btn-secondary btn-sm flex-1"
            >
              <Icon name="info" size={15} />
              Details
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/AddTradeAccount/${account.account_id}`);
              }}
              className="btn btn-secondary btn-sm flex-1"
            >
              <Icon name="edit" size={15} />
              Edit
            </button>
            {/* No Connect for paper: there is no broker to log into. The papertrade
                adapter has no /login-url endpoint, so this button could only ever fail
                — with an internal hostname and port shown to the user. */}
            {session.needsLogin && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenLoginModal(account);
                }}
                className="btn btn-primary btn-sm flex-1"
                title={isConnected ? "Refresh session" : "Connect to broker"}
              >
                {isConnected ? <Icon name="refresh" size={15} /> : <Icon name="link" size={15} />}
                {isConnected ? "Refresh" : "Connect"}
              </button>
            )}
          </div>
        </div>
    );
  };

  return (
    <div className="min-h-screen bg-bg-1 pt-0 px-4 md:px-6 pb-10">
      <div>
        {/* Standardized page header (common across all pages) */}
        <PageHeader
          title="Broker Accounts"
          subtitle="Manage your connected broker trading accounts"
          actions={
            /* No Refresh button: session status streams in over the
               broker-session WS topic (see the note at the top of this file).
               Add Account is the only action, so it no longer has to share a row
               — dropping min-w-[140px] lets it size to its label instead of
               being a fixed slab in a two-up grid on a phone. */
            <>
              {/* The whole report of a wait — first load AND refresh — is this:
                  the DS Loader's own glyph, running here and nowhere else, while
                  the content area below is left alone. Nothing unmounts, nothing
                  collapses, no layout shifts. `refreshing` (unlike `loading`) is
                  true for EVERY fetch, so this covers the cold load too, which is
                  why the 400px MUI-circle box below could go entirely. */}
              {refreshing && <Loader size="sm" label="Updating accounts" />}
              <button
                onClick={() => navigate("/AddTradeAccount")}
                className="btn btn-primary"
              >
                <Icon name="new" size={17} />
                Add Account
              </button>
            </>
          }
        />

        {/* Error Message */}
        {error && (
          <div className="mb-6">
            <AlertMessage
              severity="error"
              title="Error"
              message={error}
              onClose={() => setDismissedError(brokerSessionsError)}
            />
          </div>
        )}

        {/* NOTHING here swaps the screen out — not on refresh, and not on the
            first load either.
            It used to: the whole grid was replaced by a min-h-[400px] box holding
            an MUI <CircularProgress>. On a cold load that was the SECOND
            full-surface loader in a row, because ProtectedRoute is already showing
            "Authenticating…" over the entire app until auth settles. So the eye
            got: full-screen loader → a circle in a 400px box → cards. Two waits,
            two different spinners, a collapse and a spring — reported as "it
            actually tries to load the entire screen ... growing up and then
            shrinking ... a flashing thing". Gating the box on "no accounts YET"
            was not enough: on a cold load there are none yet, so it still fired.
            Now the content area is only ever the cards or the empty state, and
            every wait — first load and refresh alike — is reported by one quiet DS
            Loader in the header (see `actions` above). Nothing unmounts, nothing
            collapses, and the MUI circle is gone from this screen entirely.
            While the first load is in flight there is simply no content yet, so we
            render nothing rather than a placeholder that has to be taken away
            again. The empty state waits for `loading` to settle — otherwise "No
            Broker Accounts Found" would flash before the accounts arrive, which is
            worse than a blank: it is briefly a lie. */}
        {/* PullToRefresh replaces the Refresh button: same escape hatch, no
            chrome. It is a no-op on a pointer device, so desktop just gets the
            live data with nothing to click. */}
        <PullToRefresh onRefresh={fetchBrokerSessions}>
          {accounts.length === 0 ? (
            loading ? null : (
              <div className="text-center py-12 px-4">
                <div className="bg-bg-2 rounded-lg shadow-sm p-8 max-w-md mx-auto">
                  <h3 className="ds-h3 text-fg-1 mb-2">
                    No Broker Accounts Found
                  </h3>
                  <p className="text-fg-2 mb-6">
                    Get started by connecting your first broker trading account
                  </p>
                  <button
                    onClick={() => navigate("/AddTradeAccount")}
                    className="btn btn-primary btn-lg"
                  >
                    <Icon name="new" size={18} />
                    Add Your First Account
                  </button>
                </div>
              </div>
            )
          ) : (
              /* Accounts Grid.
                 Column count is capped at 3 and the track at 1400px. It used to run
                 to xl:4 / 2xl:5, which sized the CARD off the SCREEN rather than off
                 its contents: on a 1912px display that produced five 327px columns
                 holding three accounts, so the cards shrank as the monitor grew and
                 the buttons inside them cramped. An account card has a fixed amount
                 to say (holder, broker, session, three actions) and wants ~450px to
                 say it; past that, more width per card is wasted, not better. */
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 max-w-[1400px]">
                {accounts.map(renderAccountCard)}
              </div>
            )}
        </PullToRefresh>
      </div>

      {/* Login Modal */}
      {loginModalOpen && selectedAccountForLogin && (
        <LoginModal
          open={loginModalOpen}
          onClose={handleCloseLoginModal}
          account={selectedAccountForLogin}
          onLoginSuccess={handleLoginSuccess}
          isReconnect={true}
        />
      )}
    </div>
  );
};

export default TradeAccounts;
